import { useRead, ReadState, isStats, isVideos } from "@/components/LoadingStates";
import LiveDemoBadge, { isLiveCreator } from "@/components/LiveDemoBadge";
import MediaImage from "@/components/MediaImage";

import SectionTabs, { SECTION_TABS } from "@/components/SectionTabs";

import MetricCard from "@/components/MetricCard";
import VideoCard from "@/components/VideoCard";
import PlatformBadge from "@/components/PlatformBadge";
import { Card } from "@/components/ui/card";
import { Users, Video as VideoIcon, Zap, TrendingUp } from "lucide-react";
import { formatNumber } from "@/lib/format";
import { VIEWS_BY_PUBLICATION_TITLE, VIEWS_BY_PUBLICATION_NOTE, VIEWS_BY_PUBLICATION_SERIES, publicationDayLabel } from "@/lib/metricLabels";
import { Link } from "react-router-dom";
import ActivityFeed from "@/components/ActivityFeed";
import MyFollowUps from "@/components/MyFollowUps";
import { useAuth } from "@/context/AuthContext";
import {
  LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid, Area, AreaChart,
} from "recharts";

export default function Dashboard() {
  const { user } = useAuth();
  const statsRead = useRead("/dashboard/stats", isStats);
  const outliersRead = useRead("/videos?outliers_only=true&sort=outlier&limit=6", isVideos);
  const stats = statsRead.data;
  const topOutliers = outliersRead.data || [];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <SectionTabs tabs={SECTION_TABS.home} activeKey="overview" ariaLabel="Home sections" data-testid="section-tabs-home" />

      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="op-page-title">Dashboard</h1>
          <p className="op-body text-muted-foreground mt-1.5">Your creator watch across TikTok, Instagram Reels & YouTube Shorts.</p>
        </div>
      </div>

      <ReadState read={statsRead} scope="dashboard-stats" empty={stats?.total_videos === 0} title="Aucune vidéo disponible" primaryCta={{ label: "Consulter les créateurs", to: "/creators" }}>
      {stats && <>
      <p className="op-meta text-muted-foreground" data-testid="dashboard-metrics-scope">Totaux globaux : vidéos uniques. Les statistiques des profils se recoupent et ne sont pas additives ; les multiplicateurs globaux retiennent la référence maximale. « Vidéos du catalogue » compte les vidéos enregistrées, pas des analyses IA effectuées.</p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard label="Creators Tracked" value={stats.total_creators} icon={Users} accent="primary" testid="metric-creators" />
        <MetricCard label="Vidéos du catalogue" value={formatNumber(stats.total_videos)} icon={VideoIcon} accent="amber" testid="metric-videos" />
        <MetricCard label="Outliers Identified" value={stats.total_outliers} icon={Zap} accent="emerald" testid="metric-outliers" />
        <MetricCard
          label="Avg Outlier Multiplier"
          value={`${stats.avg_outlier_multiplier}x`}
          icon={TrendingUp}
          accent="rose"
          testid="metric-multiplier"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="op-section-title">{VIEWS_BY_PUBLICATION_TITLE}</h2>
              <p className="op-meta text-muted-foreground mt-0.5" data-testid="views-trend-explanation">{VIEWS_BY_PUBLICATION_NOTE}</p>
            </div>
          </div>
          <div className="h-64" data-testid="views-trend-chart">
            <ResponsiveContainer width="100%" height="100%">
              {/* T1: 12 px ticks need a less negative left margin, or the Y labels are cut by the SVG edge. */}
              <AreaChart data={stats.trend} margin={{ top: 8, right: 8, left: -6, bottom: 0 }}>
                <defs>
                  <linearGradient id="viewsGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis
                  dataKey="date"
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={12}
                  tickMargin={6}
                  tickFormatter={(d) => d.slice(5)}
                />
                <YAxis
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={12}
                  tickFormatter={(v) => formatNumber(v)}
                />
                <Tooltip
                  contentStyle={{
                    background: "hsl(var(--card))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                  labelFormatter={publicationDayLabel}
                  formatter={(v) => [formatNumber(v), VIEWS_BY_PUBLICATION_SERIES]}
                />
                <Area
                  type="monotone"
                  dataKey="views"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2}
                  fill="url(#viewsGrad)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5">
          <h2 className="op-section-title mb-4">Platform Split</h2>
          <div className="space-y-3">
            {stats.platforms.map((p) => (
              <div key={p.platform} className="space-y-1" data-testid={`platform-stat-${p.platform}`}>
                <div className="flex items-center justify-between text-sm">
                  <PlatformBadge platform={p.platform} />
                  <div className="font-mono text-xs text-muted-foreground">{p.videos} videos • {p.outliers} outliers</div>
                </div>
                <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full bg-primary"
                    style={{ width: `${(p.outliers / Math.max(p.videos, 1)) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      </>}
      </ReadState>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2" data-testid="dashboard-activity">
          {user?.workspace_id ? <ActivityFeed workspaceId={user.workspace_id} compact limit={8} headingLevel={2} /> : <MyFollowUps headingLevel={2} />}
        </div>
        {user?.workspace_id && <MyFollowUps headingLevel={2} />}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <h2 className="op-section-title">Top Outliers Right Now</h2>
            <Link to="/outliers" className="text-sm text-primary hover:underline" data-testid="see-all-outliers-link">
              See all →
            </Link>
          </div>
          <ReadState read={outliersRead} scope="dashboard-outliers" empty={!topOutliers.length} title="Aucun outlier à afficher" primaryCta={{ label: "Consulter Signals", to: "/outliers" }}>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {topOutliers.map((v) => (
              <VideoCard key={v.id} video={v} />
            ))}
          </div>
          </ReadState>
        </div>

        {statsRead.status === "success" && stats && <Card className="p-5">
          <h2 className="op-section-title mb-4">Creator Leaderboard</h2>
          <div className="space-y-3">
            {stats.leaderboard.map((c, i) => (
              <Link
                key={c.id}
                to={`/creators/${c.id}`}
                className="flex items-center gap-3 rounded-md p-2 hover:bg-accent transition-colors"
                data-testid={`leaderboard-creator-${c.id}`}
              >
                <div className="font-mono font-bold w-5 text-sm text-muted-foreground">#{i + 1}</div>
                <MediaImage src={c.avatar} alt="" name={c.name} kind="avatar" demo={!isLiveCreator(c)} className="h-9 w-9 rounded-full object-cover" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{c.name} <LiveDemoBadge creator={c} /></div>
                  <div className="text-xs text-muted-foreground truncate">{c.handle}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono text-sm font-bold text-[hsl(var(--emerald))]">{c.outlier_count}</div>
                  <div className="op-eyebrow text-muted-foreground">outliers</div>
                </div>
              </Link>
            ))}
          </div>
        </Card>}
      </div>
    </div>
  );
}
